import { format, formatDistanceToNow, isToday, isYesterday } from 'date-fns';

export const formatDate = (isoString: string) => {
  const date = new Date(isoString);

  // Extract components
  let hours = date.getHours();
  const minutes = date.getMinutes();
  const month = date.getMonth() + 1; // Months are zero-based
  const day = date.getDate();
  const year = date.getFullYear();

  // Convert to 12-hour format
  const ampm = hours >= 12 ? 'pm' : 'am';
  hours = hours % 12 || 12; // Convert 0 to 12 for 12 AM

  return `${month}/${day}/${year} - ${hours}:${minutes.toString().padStart(2, '0')}${ampm}`;
};

/**
 * Short date + time, e.g. "Jun 24, 5:16 PM".
 * Returns "Invalid date" for unparseable input.
 */
export const formatDateTime = (dateString: string) => {
  const date = new Date(dateString);
  if (isNaN(date.getTime())) {
    return 'Invalid date';
  }
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

/**
 * Time only, e.g. "5:16 PM".
 * Returns "Invalid date" for unparseable input.
 */
export const formatTime = (dateString: string) => {
  const date = new Date(dateString);
  if (isNaN(date.getTime())) {
    return 'Invalid date';
  }
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

/**
 * Day-group label: "Today · Jul 10", "Yesterday", "Jun 30", or
 * "Jun 30, 2025" for prior years. Returns "Unknown date" for
 * unparseable input.
 */
export const formatDayLabel = (dateString: string) => {
  const date = new Date(dateString);
  if (isNaN(date.getTime())) {
    return 'Unknown date';
  }
  if (isToday(date)) {
    return `Today · ${format(date, 'MMM d')}`;
  }
  if (isYesterday(date)) {
    return 'Yesterday';
  }
  return format(
    date,
    date.getFullYear() !== new Date().getFullYear() ? 'MMM d, yyyy' : 'MMM d',
  );
};

export const safeFormatDate = (
  date: string | number | Date,
  dateFormat: string,
) => {
  try {
    return format(date, dateFormat);
  } catch {
    return '';
  }
};

function abbreviateDistanceToNow(distance: string): string {
  let s = distance.replace(/^about /, '');
  s = s.replace(/less than a minute/, '<1m');
  s = s.replace(/\ba minute\b/, '1m');
  s = s.replace(/(\d+) minutes?/, '$1m');
  s = s.replace(/\ban hour\b/, '1h');
  s = s.replace(/(\d+) hours?/, '$1h');
  s = s.replace(/\ba day\b/, '1d');
  s = s.replace(/(\d+) days?/, '$1d');
  s = s.replace(/\ba week\b/, '1w');
  s = s.replace(/(\d+) weeks?/, '$1w');
  s = s.replace(/\ba month\b/, '1mo');
  s = s.replace(/(\d+) months?/, '$1mo');
  s = s.replace(/\ba year\b/, '1y');
  s = s.replace(/(\d+) years?/, '$1y');
  return s;
}

/**
 * Compact relative time, e.g. "5m ago", "2h ago", "3d ago".
 * Returns empty string for null/undefined/invalid dates.
 */
export function formatTimeAgo(
  dateInput: string | number | Date | null | undefined,
): string {
  if (dateInput == null) return '';
  const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
  if (Number.isNaN(date.getTime())) return '';

  const distance = formatDistanceToNow(date, { addSuffix: true });
  return abbreviateDistanceToNow(distance);
}
